import * as React from 'react';
import cn from 'classnames';
import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { GraphQLError } from 'graphql';

import { InfoMessageTypeEnum } from '@websky/graphql';
import { InputVariant } from '../../../BaseComponents/Input/types';
import { BaseButton, Input, ModalX, Tooltip } from '../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { useTheme } from '../../../theme';
import { MOBILE_MIN_WIDTH, useIsTablet } from '../../../utils';
import ClarifyDocumentModal from '../../../RetrieveBooking/components/ClarifyDocumentModal/ClarifyDocumentModal';
import { useClarifyDocumentModal } from '../../../RetrieveBooking/components/ClarifyDocumentModal/hooks';
import { useRetrieveBookingValidation } from '../../../RetrieveBooking/hooks';
import { isDifferentCurrenciesError } from '../../../Modules/Checkin/components/Checkin/Order/Passengers/utils';
import Notification from '../../../Notification';

interface Props {
	onSubmit: (lastName: string, id: string, extraSecret?: string) => Promise<boolean>;
	onSuccess: () => void;
	onCancel?: () => void;
	initialValues?: {
		lastName: string;
		id: string;
	};
	hideIcon?: boolean;
	buttonText?: string;
	inputVariant?: InputVariant;
	validationType?: 'common' | 'ticketNumber';
}

enum ButtonState {
	Common,
	Loading,
	Success
}

const AddPassengerForm: React.FC<Props> = ({
	onSubmit,
	onSuccess,
	onCancel,
	initialValues,
	hideIcon,
	buttonText,
	validationType,
	inputVariant
}) => {
	const { t } = useTranslation('AddPassenger');
	const css = useTheme('AddPassenger').AddPassenger;

	const [isError, setIsError] = useState(false);
	const [isOpenModalInfo, setIsOpenModalInfo] = useState(false);
	const [buttonState, setButtonState] = useState(ButtonState.Common);

	const isMobile = useMediaQuery({ maxWidth: +MOBILE_MIN_WIDTH + 1 });
	const isTablet = useIsTablet();

	const {
		isOpen,
		open: openModal,
		searchParams,
		setSearchParams,
		close: closeModal,
		needToShowClarifyModal
	} = useClarifyDocumentModal();

	const { lastNameValidation, ticketNumberValidation, ticketNumberFormat } = useRetrieveBookingValidation(
		validationType
	);

	const submitForm = useCallback(
		async (values: object) => await onSubmit(values['lastName'], values['id'], values['extraSecret']),
		[onSubmit]
	);

	const onSubmitHandler = useCallback(
		async (values: object) => {
			setButtonState(ButtonState.Loading);

			try {
				const result = await submitForm(values);

				if (result) {
					setButtonState(ButtonState.Success);

					setTimeout(() => {
						onSuccess();
						setButtonState(ButtonState.Common);
					}, 2000);
				} else {
					setButtonState(ButtonState.Common);
					setIsError(true);
				}
			} catch (e) {
				setButtonState(ButtonState.Common);

				if (needToShowClarifyModal(e as Error)) {
					setSearchParams(values);
					openModal();
				} else if (isDifferentCurrenciesError(e as GraphQLError)) {
					setIsError(false);
				} else {
					setIsError(true);
				}
			}
		},
		[submitForm]
	);

	const handleClarifySubmit = useCallback(
		async (values: object) => {
			try {
				const result = await submitForm(values);

				if (result) {
					setButtonState(ButtonState.Success);

					setTimeout(() => {
						onSuccess();
						setButtonState(ButtonState.Common);
					}, 2000);
				}

				return result;
			} catch (e) {
				console.warn(e);

				return false;
			}
		},
		[submitForm]
	);

	return (
		<>
			<ClarifyDocumentModal
				searchParams={searchParams}
				onSubmit={handleClarifySubmit}
				open={isOpen}
				onClose={closeModal}
			/>

			<Form
				onSubmit={onSubmitHandler}
				initialValues={initialValues}
				render={({ handleSubmit, errors, touched }) => {
					const isIdFieldError = !!errors?.id && touched?.id;

					return (
						<form className={css.form} onSubmit={handleSubmit}>
							<div className={css.fields}>
								{isMobile && isError && (
									<Notification
										type="context"
										className={css.notification}
										contextClassName={css.notification_context}
										textClassName={css.notification__text}
										status={InfoMessageTypeEnum.Warning}
										infoMessage={{
											type: InfoMessageTypeEnum.Warning,
											text: t('Passenger not found'),
											code: 'add-passenger-error'
										}}
									/>
								)}

								<Field
									name="lastName"
									validate={(value: string, allValues, meta): string | void => {
										if (!value) {
											return t('Required field') as string;
										}

										return lastNameValidation(value);
									}}
									render={({ input, meta }) => (
										<Input
											className={css.form__input}
											name="lastName"
											label={t('Passenger last name')}
											variant={inputVariant}
											InputProps={input}
											error={!!meta.error && meta.touched}
											helperText={!!meta.error && meta.touched ? meta.error : ''}
											disabled={
												buttonState === ButtonState.Loading ||
												buttonState === ButtonState.Success
											}
										/>
									)}
								/>

								<Field
									name="id"
									parse={ticketNumberFormat}
									validate={(value: string): string | void => {
										if (!value) {
											return t('Required field') as string;
										}

										return ticketNumberValidation(value);
									}}
									render={({ input, meta }) => (
										<Input
											className={css.form__input}
											name="id"
											label={t(
												validationType === 'ticketNumber'
													? 'Ticket number'
													: 'Ticket number or booking reference'
											)}
											variant={inputVariant}
											InputProps={input}
											error={(!!meta.error && meta.touched) || (!isMobile && isError)}
											helperText={
												!!meta.error && meta.touched
													? meta.error
													: !isMobile && isError
													? t('Passenger not found')
													: ''
											}
											disabled={
												buttonState === ButtonState.Loading ||
												buttonState === ButtonState.Success
											}
										/>
									)}
								/>
								{!isTablet ? (
									<Tooltip title={t('TicketInfoInReceipt')}>
										<div className={cn(css.infoIcon, { [css.infoIcon__error]: isIdFieldError })}>
											<CdnIcon icon={'Info/Question'} />
										</div>
									</Tooltip>
								) : (
									<>
										<div
											className={cn(css.infoIcon, { [css.infoIcon__error]: isIdFieldError })}
											onClick={() => setIsOpenModalInfo(true)}
										>
											<CdnIcon icon={'Info/Question'} />
										</div>
										<ModalX
											isOpen={isOpenModalInfo}
											onClose={() => setIsOpenModalInfo(false)}
											title={
												<span className={css.infoModalTitle}>
													{t('WhereToFindTicketNumber')}
												</span>
											}
										>
											{t('TicketInfoInReceipt')}
										</ModalX>
									</>
								)}
							</div>

							<div className={css.buttons}>
								{onCancel && (
									<BaseButton
										type="button"
										className={css.cancelButton}
										variant={isMobile ? 'smallSecondary' : 'optionAction'}
										onClick={onCancel}
									>
										{t('Cancel')}
									</BaseButton>
								)}
								<BaseButton
									type="submit"
									className={css.submitButton}
									variant={isMobile ? 'smallPrimary' : 'optionAction'}
									isLoading={buttonState === ButtonState.Loading}
								>
									{buttonText || t('Find')}
								</BaseButton>
							</div>
						</form>
					);
				}}
			/>
		</>
	);
};

export default AddPassengerForm;
